import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper to render HTML content safely
const renderHTML = (html) => {
  if (!html) return null;
  // Check if the content contains HTML tags
  if (/<[a-z][\s\S]*>/i.test(html)) {
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return html;
};

const defaultFeatures = [
  "Unlimited database connections",
  "All 12 database types supported",
  "Query history & saved queries",
  "CSV & JSON export",
  "Visual database explorer",
  "In-cell CRUD operations",
  "Performance metrics",
  "Priority email support",
  "Cancel anytime - no commitment"
];

const Pricing = ({
  title = "Buy Credits",
  subtitle = "Start with flexible credit packages",
  primaryColor = "#22c55e",
  pricingFeatures = defaultFeatures,
  fallbackPackages = [],
  fallbackCostPerQuery = 1,
  onCtaClick
}) => {
  const [packages, setPackages] = useState(fallbackPackages);
  const [costPerQuery, setCostPerQuery] = useState(fallbackCostPerQuery);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API_URL}/site-settings/credit-packages`);
        if (response?.data) {
          setPackages(response.data.packages || []);
          setCostPerQuery(response.data.credit_cost_per_query ?? fallbackCostPerQuery);
          setHasFetched(true);
        }
      } catch (error) {
        console.error("Failed to fetch credit packages");
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, [fallbackCostPerQuery]);

  useEffect(() => {
    if (!hasFetched) {
      setPackages(fallbackPackages);
      setCostPerQuery(fallbackCostPerQuery);
    }
  }, [fallbackPackages, fallbackCostPerQuery, hasFetched]);

  const visibleFeatures = pricingFeatures.length ? pricingFeatures : defaultFeatures;

  return (
    <section id="pricing" className="py-20 px-4 border-t border-border" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="pricing-title">
            {renderHTML(title)}
          </h2>
          {subtitle && (
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="pricing-subtitle">
              {renderHTML(subtitle)}
            </p>
          )}
          <p className="text-base text-muted-foreground mt-4" data-testid="pricing-cost-per-query">
            Each query costs {costPerQuery} credit{costPerQuery !== 1 ? 's' : ''}. Purchase credits to run unlimited queries.
          </p>
        </div>

        {packages.length === 0 && !loading ? (
          <div className="text-center text-sm text-muted-foreground" data-testid="pricing-empty-state">
            No credit packages are available right now. Please check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-testid="pricing-grid">
            {packages.map((pkg, idx) => {
              const perCredit = pkg.credits > 0 ? (pkg.price / pkg.credits).toFixed(3) : 0;
              const isBest = idx === packages.length - 1;

              return (
                <div
                  key={idx}
                  className={`rounded-2xl border-2 p-6 bg-card relative overflow-hidden flex flex-col ${
                    isBest ? 'scale-105 shadow-xl shadow-primary/10 z-10' : ''
                  }`}
                  style={{ borderColor: isBest ? primaryColor : 'var(--border)' }}
                  data-testid={`pricing-card-${idx}`}
                >
                  {isBest && (
                    <div
                      className="absolute top-0 right-0 px-4 py-1 text-xs font-bold uppercase tracking-wider rounded-bl-lg"
                      style={{ backgroundColor: primaryColor, color: '#000' }}
                      data-testid={`pricing-card-badge-${idx}`}
                    >
                      Best Value
                    </div>
                  )}

                  <div className="text-center mb-6 mt-2">
                    <p className="text-4xl font-bold" data-testid={`pricing-card-credits-${idx}`}>
                      {pkg.credits.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground uppercase font-semibold tracking-wider mt-1" data-testid={`pricing-card-credits-label-${idx}`}>
                      credits
                    </p>
                  </div>

                  <div className="text-center py-4 border-y border-border/50 mb-6 flex-1">
                    <p className="text-3xl font-bold mb-1" data-testid={`pricing-card-price-${idx}`}>
                      ${pkg.price.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`pricing-card-per-credit-${idx}`}>
                      ${perCredit} per credit
                    </p>
                    <p className="text-sm font-medium text-muted-foreground mt-4" data-testid={`pricing-card-query-estimate-${idx}`}>
                      ~{Math.floor(pkg.credits / costPerQuery).toLocaleString()} queries
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6" data-testid={`pricing-features-list-${idx}`}>
                    {visibleFeatures.slice(0, 4).map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2 text-xs" data-testid={`pricing-feature-${idx}-${fIdx}`}>
                        <Check className="w-3 h-3 flex-shrink-0" style={{ color: primaryColor }} />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full mt-auto"
                    size="lg"
                    onClick={() => onCtaClick?.(pkg, idx)}
                    style={isBest ? { backgroundColor: primaryColor, color: '#000' } : {}}
                    variant={isBest ? "default" : "outline"}
                    data-testid={`pricing-cta-btn-${idx}`}
                  >
                    Buy {pkg.label}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Pricing;
